<template>
  <div class="video-block" :style="containerStyles">
    <div class="video-wrapper" :style="wrapperStyles">
      <a 
        :href="videoUrl || '#'" 
        class="video-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        <!-- Imagen de portada -->
        <div class="video-thumbnail" :style="thumbnailStyles">
          <img 
            :src="thumbnailUrl || defaultThumbnail" 
            :alt="altText"
            :style="imageStyles"
            @error="handleImageError"
          >
          
          <!-- Overlay de play -->
          <div class="play-overlay" :style="overlayStyles">
            <div class="play-button" :style="playButtonStyles">
              <i class="fas fa-play" :style="playIconStyles"></i>
            </div>
          </div>
        </div>
        
        <!-- Título del video -->
        <div v-if="title" class="video-title" :style="titleStyles">
          {{ title }}
        </div>
        
        <!-- Descripción -->
        <div v-if="description" class="video-description" :style="descriptionStyles">
          {{ description }}
        </div>
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoBlock',
  props: {
    videoUrl: {
      type: String,
      default: '#'
    },
    thumbnailUrl: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: 'Ver Video'
    },
    description: {
      type: String,
      default: ''
    },
    altText: {
      type: String,
      default: 'Video thumbnail'
    },
    width: {
      type: String,
      default: '100%'
    },
    maxWidth: {
      type: String,
      default: '600px'
    },
    align: {
      type: String,
      default: 'center'
    },
    borderRadius: {
      type: String,
      default: '8px'
    },
    backgroundColor: {
      type: String,
      default: 'transparent'
    },
    padding: {
      type: String,
      default: '20px'
    },
    playButtonColor: {
      type: String,
      default: '#ffffff'
    },
    playButtonBackground: {
      type: String,
      default: 'rgba(0, 0, 0, 0.7)'
    },
    titleColor: {
      type: String,
      default: '#2c3e50'
    },
    titleSize: {
      type: String,
      default: '18px'
    },
    descriptionColor: {
      type: String,
      default: '#6c757d'
    },
    descriptionSize: {
      type: String,
      default: '14px'
    }
  },
  computed: {
    defaultThumbnail() {
      return 'https://via.placeholder.com/600x337/f8f9fa/6c757d?text=Video+Thumbnail'
    },
    containerStyles() {
      return {
        backgroundColor: this.backgroundColor,
        padding: this.padding,
        textAlign: this.align
      }
    },
    wrapperStyles() {
      return {
        display: 'inline-block',
        width: this.width,
        maxWidth: this.maxWidth,
        borderRadius: this.borderRadius,
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease'
      }
    },
    thumbnailStyles() {
      return {
        position: 'relative',
        width: '100%',
        paddingBottom: '56.25%', // 16:9 aspect ratio
        overflow: 'hidden',
        cursor: 'pointer'
      }
    },
    imageStyles() {
      return {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform 0.3s ease'
      }
    },
    overlayStyles() {
      return {
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        transition: 'all 0.3s ease'
      }
    },
    playButtonStyles() {
      return {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: this.playButtonBackground,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)'
      }
    },
    playIconStyles() {
      return {
        color: this.playButtonColor,
        fontSize: '32px',
        marginLeft: '4px' // Pequeño ajuste visual para centrar el triángulo
      }
    },
    titleStyles() {
      return {
        color: this.titleColor,
        fontSize: this.titleSize,
        fontWeight: '600',
        margin: '15px 15px 8px 15px',
        textAlign: 'left'
      }
    },
    descriptionStyles() {
      return {
        color: this.descriptionColor,
        fontSize: this.descriptionSize,
        margin: '0 15px 15px 15px',
        textAlign: 'left',
        lineHeight: '1.5'
      }
    }
  },
  methods: {
    handleImageError(e) {
      e.target.src = this.defaultThumbnail
    }
  }
}
</script>

<style scoped>
.video-block {
  width: 100%;
  margin: 10px 0;
}

.video-link {
  text-decoration: none;
  color: inherit;
}

.video-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
}

.video-wrapper:hover .play-overlay {
  background-color: rgba(0, 0, 0, 0.5);
}

.video-wrapper:hover .play-button {
  transform: scale(1.1);
}

.video-wrapper:hover img {
  transform: scale(1.05);
}

/* Responsive para emails */
@media only screen and (max-width: 600px) {
  .video-wrapper {
    width: 100% !important;
    max-width: 100% !important;
  }
  
  .play-button {
    width: 60px !important;
    height: 60px !important;
  }
  
  .play-button i {
    font-size: 24px !important;
  }
  
  .video-title {
    font-size: 16px !important;
  }
  
  .video-description {
    font-size: 13px !important;
  }
}

/* Estilos específicos para clientes de email */
.video-thumbnail {
  mso-line-height-rule: exactly;
}

.video-title,
.video-description {
  mso-line-height-rule: exactly;
}
</style> 